import { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Box, Column } from '@/components/lib/components';
import { resultMarketTypes, sortOptions } from '@/components/sbk-traditional-matchup-card/utils';
import { OptionButton, getBorderRadiusStyle } from '@/feature/event-details-sbk/components/OptionButton';
import { useMainLineMarket, useMarketCacheStore } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { Event, Market, MarketType } from '@/feature/event-details-sbk/types';
import { TeamOrder } from '@/utils/sport-utils';
import { useShallow } from 'zustand/react/shallow';

export const MARKET_COLUMN_WIDTH = 68;

type MarketColumnProps = {
    header: string;
    event: Event;
    markets: Market[];
    marketType: MarketType;
    showHeader: boolean;
    columnIndex: number;
    totalColumns: number;
    teamOrder: TeamOrder;
};

export const MarketColumn = ({
    header,
    event,
    markets,
    marketType,
    showHeader,
    columnIndex,
    totalColumns,
    teamOrder,
}: MarketColumnProps) => {
    const currentMainLineMarket = useMainLineMarket(event.id, marketType.code);

    // Find the market to display, either the main line market or the first market
    const activeMarket = useMarketCacheStore(
        useShallow(state => {
            return currentMainLineMarket ?? markets.find(m => state.markets[m.id]?.isMainLine) ?? markets[0];
        })
    );

    const options = useMemo(() => {
        return activeMarket?.options.toSorted((a, b) => sortOptions(a, b, marketType, teamOrder));
    }, [activeMarket, marketType, teamOrder]);

    const expectedSelections = marketType.params?.selections ?? 2;

    if (!options || options.length < expectedSelections) {
        return null;
    }

    const totalRows = options.length;

    // Hide the option name for RESULT/Winner markets. We only show the option name for totals/spread markets.
    const hideOptionName = resultMarketTypes.includes(marketType.code);

    return (
        <Column width={MARKET_COLUMN_WIDTH}>
            {showHeader ? (
                <Text variant="bodySmall" color="neutral300" textAlign="center">
                    {header}
                </Text>
            ) : null}
            <Box gap="s3" mt={showHeader ? 's12' : 's20'}>
                {options.map((option, rowIndex) => (
                    <OptionButton
                        key={option.id}
                        option={option}
                        market={activeMarket}
                        event={event}
                        isShortName
                        buttonStyle={[
                            styles.optionButton,
                            getBorderRadiusStyle(rowIndex, columnIndex, totalRows, totalColumns),
                        ]}
                        hideOptionName={hideOptionName}
                    />
                ))}
            </Box>
        </Column>
    );
};

export const styles = StyleSheet.create({
    optionButton: {
        paddingHorizontal: 8,
        paddingVertical: 11,
    },
});
